// Ящик уведомлений: приглашения в игру и новости об обновлениях. Хранится в localStorage (переживает
// перезагрузку), непрочитанные считаются для колокольчика 🔔. Всплывающая лента (notify.ts) — мгновенный
// сигнал, ящик — то, к чему можно вернуться. Новости можно отключить; приглашения приходят всегда.
// Системные уведомления браузера (Notification API) — отдельно и только по разрешению: для приглашений,
// когда вкладка не на виду.
import { CHANGELOG, latestChangelog, changelogVersion, fmtDate } from './changelog.ts';

export type InboxKind = 'invite' | 'news' | 'ach' | 'corr';

export interface InboxItem {
  id: string;
  kind: InboxKind;
  /** когда пришло (мс) */
  at: number;
  title: string;
  text: string;
  /** приглашение: код комнаты; партия по переписке: код партии */
  code?: string;
  /** новость: версия (дата) ченджлога */
  version?: string;
  read: boolean;
}

const INBOX_KEY = 'krutagidon-inbox-v1';
const NEWS_KEY = 'krutagidon-news';
const SEEN_KEY = 'krutagidon-changelog-seen';
const MAX_ITEMS = 50;

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}
function writeJson(key: string, v: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(v));
  } catch {
    /* приватный режим / переполнение — ящик необязателен */
  }
}
function readStr(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeStr(key: string, v: string): void {
  try {
    localStorage.setItem(key, v);
  } catch {
    /* ignore */
  }
}

function isItem(x: unknown): x is InboxItem {
  if (!x || typeof x !== 'object') return false;
  const i = x as Partial<InboxItem>;
  return typeof i.id === 'string' && (i.kind === 'invite' || i.kind === 'news' || i.kind === 'ach' || i.kind === 'corr') && typeof i.at === 'number' && typeof i.title === 'string' && typeof i.text === 'string';
}

let cache: InboxItem[] | null = null;
const listeners: (() => void)[] = [];

/** Все уведомления, новые первыми */
export function inboxItems(): InboxItem[] {
  if (!cache) {
    const raw = readJson<unknown>(INBOX_KEY, []);
    cache = Array.isArray(raw) ? raw.filter(isItem).map((i) => ({ ...i, read: !!i.read })) : [];
  }
  return cache;
}

function commit(items: InboxItem[]): void {
  items.sort((a, b) => b.at - a.at);
  while (items.length > MAX_ITEMS) items.pop();
  cache = items;
  writeJson(INBOX_KEY, items);
  for (const l of listeners) l();
}

/** Подписка на изменения ящика (колокольчик перерисовывается) */
export function onInboxChange(cb: () => void): void {
  listeners.push(cb);
}

export function unreadCount(): number {
  return inboxItems().filter((i) => !i.read).length;
}

/** Положить уведомление; повтор по id заменяет старое (новое приглашение в ту же комнату — одно, свежее) */
export function pushItem(item: InboxItem): void {
  commit([item, ...inboxItems().filter((i) => i.id !== item.id)]);
}

export function markAllRead(): void {
  if (!unreadCount()) return;
  commit(inboxItems().map((i) => ({ ...i, read: true })));
}

/**
 * Прочитано там, куда ведёт уведомление: новость — открыли «Что нового», ачивки — профиль, приглашение и ход по
 * переписке — вошли в комнату. Иначе колокольчик показывал бы непрочитанным то, что игрок уже видел.
 */
export function markRead(pred: (i: InboxItem) => boolean): void {
  if (!inboxItems().some((i) => !i.read && pred(i))) return;
  commit(inboxItems().map((i) => (!i.read && pred(i) ? { ...i, read: true } : i)));
}

export function removeItem(id: string): void {
  commit(inboxItems().filter((i) => i.id !== id));
}

export function clearInbox(): void {
  commit([]);
}

// ---------- новости ----------

export function newsEnabled(): boolean {
  return readStr(NEWS_KEY) !== '0';
}
export function setNewsEnabled(on: boolean): void {
  writeStr(NEWS_KEY, on ? '1' : '0');
}

/** Уведомление о версии ченджлога */
export function newsItem(version: string): InboxItem | null {
  const e = CHANGELOG.find((c) => changelogVersion(c) === version);
  if (!e) return null;
  const head = e.items.slice(0, 2).map((s) => s.replace(/[.:].*$/, '')).join(' · ');
  return { id: `news:${version}`, kind: 'news', at: Date.now(), title: `Обновление ${fmtDate(e.date)}: ${e.title}`, text: `${head}${e.items.length > 2 ? ' и ещё' : ''} — подробности в «Что нового».`, version, read: false };
}

/**
 * Проверка при запуске: появилась ли новая запись ченджлога с прошлого раза. Первый запуск на устройстве
 * (firstRun) — ничего не сообщать, только запомнить версию. Возвращает положенное уведомление (или null).
 */
export function checkChangelog(firstRun: boolean, now = Date.now()): InboxItem | null {
  const latest = changelogVersion(latestChangelog());
  const seen = readStr(SEEN_KEY);
  writeStr(SEEN_KEY, latest);
  if (firstRun || seen === latest || !newsEnabled()) return null;
  const item = newsItem(latest);
  if (!item) return null;
  item.at = now;
  pushItem(item);
  return item;
}

/** Приглашение в комнату — в ящик (повтор от того же в ту же комнату заменяет старое) */
export function inviteItem(from: string, code: string, players: readonly string[], now = Date.now()): InboxItem {
  const who = players.filter((p) => p !== from);
  return { id: `invite:${code}`, kind: 'invite', at: now, title: `${from} зовёт в игру`, text: `Комната ${code}${who.length ? ` · уже там: ${who.join(', ')}` : ''}`, code, read: false };
}

/** Новые ачивки за партию (seed) — в ящик; names — названия; повтор той же партии заменяет прежнее */
export function achItem(seed: number, names: readonly string[], now = Date.now()): InboxItem {
  return {
    id: `ach:${seed}`, kind: 'ach', at: now, read: false,
    title: names.length === 1 ? '🏅 Новая ачивка' : `🏅 Новые ачивки: ${names.length}`,
    text: `${names.join(', ')} — все ачивки и как их получить — в профиле.`,
  };
}

/** Партия по переписке ждёт меня (уведомление пришло, пока приложение на экране); повтор той же партии заменяет прежнее */
export function corrItem(code: string, title: string, text: string, now = Date.now()): InboxItem {
  return { id: `corr:${code}`, kind: 'corr', at: now, title: `✉️ ${title}`, text, code, read: false };
}

/** Уведомление с этим id уже в ящике */
export function hasItem(id: string): boolean {
  return inboxItems().some((i) => i.id === id);
}

// ---------- системные уведомления браузера ----------

export type SysPermission = 'granted' | 'denied' | 'default' | 'unsupported';

export function sysPermission(): SysPermission {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

export async function requestSysPermission(): Promise<SysPermission> {
  if (typeof Notification === 'undefined') return 'unsupported';
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

/** Показать системное уведомление (через service worker в установленном приложении, иначе — напрямую) */
export function sysNotify(title: string, body: string, tag: string): void {
  if (sysPermission() !== 'granted') return;
  const opts: NotificationOptions = { body, tag, icon: 'assets/icons/prize-192.png' };
  const direct = (): void => {
    try {
      const n = new Notification(title, opts);
      n.onclick = () => {
        window.focus();
        n.close();
      };
    } catch {
      /* ignore */
    }
  };
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.ready.then((reg) => reg.showNotification(title, opts)).catch(direct);
  } else direct();
}

/** Сбросить кэш (тесты с подменённым localStorage) */
export function resetInboxCache(): void {
  cache = null;
}
